import { useCallback, useRef, useState } from "react";
import { startStepUp, verifyStepUp } from "../../../services/mfa/mfaSettingsApi";
import { describeMfaError } from "../../../services/mfa/mfaErrorMessages";

const CODE_ERROR = "Enter the 6-digit code.";

// `methods` overrides the choice (Change Password V2 passes ["totp"] for an ACTIVE authenticator: Oracle refuses an
// email step-up token for a password change then; docs/specs/password-reset-stepup.md).
const useStepUp = ({ hasTotp, methods: only }) => {
  const methods = only || (hasTotp ? ["totp", "email"] : ["email"]);
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const txRef = useRef(null);
  const busyRef = useRef(false);
  const resolveRef = useRef(null);

  const start = useCallback(async (nextMethod) => {
    setBusy(true);
    setError(null);
    try {
      const response = await startStepUp({ method: nextMethod });
      if (!response?.step_up_tx) throw new Error("Missing step-up transaction.");
      txRef.current = response.step_up_tx;
    } catch (e) {
      setError(describeMfaError(e).message);
    } finally {
      setBusy(false);
    }
  }, []);

  const begin = useCallback(() => {
    const nextMethod = hasTotp ? "totp" : "email";
    setOpen(true);
    setMethod(nextMethod);
    setError(null);
    txRef.current = null;
    const result = new Promise((resolve) => {
      resolveRef.current = resolve;
    });
    start(nextMethod);
    return result;
  }, [hasTotp, start]);

  const selectMethod = useCallback((nextMethod) => {
    setMethod(nextMethod);
    txRef.current = null;
    start(nextMethod);
  }, [start]);

  // "Resend OTP" in an email step-up: a new step-up for the same method mails a new code (new tx).
  const resend = useCallback(() => {
    txRef.current = null;
    return start(method);
  }, [method, start]);

  const submit = useCallback(async (raw) => {
    if (!/^[0-9]{6}$/.test(raw)) {
      setError(CODE_ERROR);
      return;
    }
    if (busyRef.current) return;

    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await verifyStepUp({ stepUpTx: txRef.current, code: raw });
      if (!response?.step_up_token) throw new Error("Missing step-up token.");
      txRef.current = null;
      setOpen(false);
      resolveRef.current?.(response.step_up_token);
      resolveRef.current = null;
    } catch (e) {
      setError(describeMfaError(e).message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const cancel = useCallback(() => {
    txRef.current = null;
    setOpen(false);
    setError(null);
    resolveRef.current?.(null);
    resolveRef.current = null;
  }, []);

  return { open, method, methods, busy, error, begin, selectMethod, resend, submit, cancel };
};

export default useStepUp;
